import { useState } from 'react';
import { FileDown, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Entry, AISummary } from '../lib/types';

interface ExportButtonProps {
  entries: Entry[];
  summary: AISummary | null;
  topic: string;
}

export function ExportButton({ entries, summary, topic }: ExportButtonProps) {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);

  const generateTxtContent = () => {
    const lines = [
      `${t('export.title')}: ${topic}`,
      `${t('export.exported_at')}: ${new Date().toLocaleString()}`,
      `\n--- ${t('export.entries')} ---\n`
    ];

    // Group entries by letter
    const entriesByLetter = entries.reduce((acc, entry) => {
      const letter = entry.letter.toUpperCase();
      if (!acc[letter]) acc[letter] = [];
      acc[letter].push(entry);
      return acc;
    }, {} as Record<string, Entry[]>);

    // Add entries sorted by letter
    Object.entries(entriesByLetter)
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([letter, letterEntries]) => {
        lines.push(`\n${t('export.letter')} ${letter}:`);
        letterEntries.forEach(entry => {
          lines.push(`- ${entry.content}`);
        });
      });

    // Add AI summary if available
    if (summary?.content) {
      lines.push(`\n\n--- ${t('export.ai_analysis')} ---\n`);
      lines.push(summary.content.replace(/<[^>]*>/g, '')); // Remove HTML tags
    }

    return lines.join('\n');
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const content = generateTxtContent();
      
      // Create blob and download
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `brainstorming-${topic.toLowerCase().replace(/[^a-z0-9]/g, '-')}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting file:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <button
      onClick={handleExport}
      disabled={isExporting}
      className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors disabled:opacity-50"
    >
      {isExporting ? (
        <>
          <Check className="h-4 w-4" />
          {t('export.success')}
        </>
      ) : (
        <>
          <FileDown className="h-4 w-4" />
          {t('export.download')}
        </>
      )}
    </button>
  );
}